// src/pages/HowItWorks.js
import React from 'react';
import { Link } from 'react-router-dom';
import './HowItWorks.css';

const HowItWorks = () => {
  const steps = [
    {
      id: 1,
      number: '01',
      icon: '🔍',
      title: 'Browse & Discover',
      description:
        'Open the QSlot app and explore car washes, turf courts, and tournaments near you. Filter by location, price, and rating to find the perfect match.',
      features: [
        'Location-based search',
        'Real-time availability',
        'Verified vendors only',
      ],
      color: '#4f46e5',
    },
    {
      id: 2,
      number: '02',
      icon: '📅',
      title: 'Choose Your Slot',
      description:
        'Pick your preferred date, time, and service. Whether it\'s a car wash, a 90-minute turf match, or a tournament registration — it takes just a few taps.',
      features: [
        'Flexible date & time picker',
        'Instant slot confirmation',
        'Customizable service options',
      ],
      color: '#10b981',
    },
    {
      id: 3,
      number: '03',
      icon: '💳',
      title: 'Pay Securely',
      description:
        'Make secure and convenient payments through the QSlot App using your preferred payment method, with instant payment confirmation',
      features: [
        'UPI / Credit & Debit Cards',
        'Secure Payment Processing',
        'Instant payment confirmation',
      ],
      color: '#f59e0b',
    },
    {
      id: 4,
      number: '04',
      icon: '🎉',
      title: 'Enjoy Your Service',
      description:
        'Get instant confirmation via SMS & email. Show up at the venue or wait for your car wash — everything is handled. Rate your experience after.',
      features: [
        'SMS & email confirmation',
        'In-app booking history',
        'Rate & review partners',
      ],
      color: '#ef4444',
    },
  ];

  return (
    <div className="howitworks-page">
      {/* Page Header */}
      <header className="howitworks-header">
        <div className="howitworks-header-content">
          <Link to="/" className="howitworks-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Home
          </Link>

          <h1 className="howitworks-title">
            Book in <span className="howitworks-title-highlight">4 Simple Steps</span>
          </h1>
          <p className="howitworks-subtitle">
            From discovering the perfect service to enjoying your booking —
            here's how QSlot makes everything effortless.
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="howitworks-container">
        {/* Steps */}
        <div className="howitworks-steps">
          {steps.map((step, index) => (
            <div key={step.id} className="howitworks-step-wrapper">
              {/* Step Card */}
              <div className="howitworks-step-card">
                {/* Number Badge */}
                <div
                  className="howitworks-step-number"
                  style={{ backgroundColor: `${step.color}15`, color: step.color }}
                >
                  {step.number}
                </div>

                {/* Icon */}
                <div
                  className="howitworks-step-icon"
                  style={{
                    backgroundColor: `${step.color}15`,
                    color: step.color,
                  }}
                >
                  <span className="howitworks-step-icon-emoji">{step.icon}</span>
                </div>

                {/* Title */}
                <h3 className="howitworks-step-title">{step.title}</h3>

                {/* Description */}
                <p className="howitworks-step-description">
                  {step.description}
                </p>

                {/* Features */}
                <ul className="howitworks-step-features">
                  {step.features.map((feature, i) => (
                    <li key={i} className="howitworks-step-feature">
                      <span
                        className="howitworks-step-check"
                        style={{ color: step.color }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Connector Arrow (between cards) */}
              {index < steps.length - 1 && (
                <div className="howitworks-connector" aria-hidden="true">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Additional Info Section */}
        <div className="howitworks-info">
          <div className="howitworks-info-grid">
            <div className="howitworks-info-card">
              <span className="howitworks-info-icon">⚡</span>
              <h4 className="howitworks-info-title">Instant Booking</h4>
              <p className="howitworks-info-text">
                No calls, no waiting. Confirm your slot in under 30 seconds.
              </p>
            </div>
            <div className="howitworks-info-card">
              <span className="howitworks-info-icon">🔒</span>
              <h4 className="howitworks-info-title">100% Secure</h4>
              <p className="howitworks-info-text">
                Bank-grade encryption protects every transaction you make.
              </p>
            </div>
            <div className="howitworks-info-card">
              <span className="howitworks-info-icon">🎯</span>
              <h4 className="howitworks-info-title">Verified Partners</h4>
              <p className="howitworks-info-text">
                Every vendor is background-checked and quality-approved.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="howitworks-cta">
          <h2 className="howitworks-cta-title">Ready to Book Your First Slot?</h2>
          <p className="howitworks-cta-text">
            Download the QSlot app and start booking car washes, turfs, and
            tournaments today.
          </p>
          <div className="howitworks-cta-buttons">
            <a
              href="https://apps.apple.com/us/app/qslot/id6747074785"
              target="_blank"
              rel="noopener noreferrer"
              className="howitworks-cta-btn"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              App Store
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.fewas.qslot.user.app&hl=en-US&pli=1"
              target="_blank"
              rel="noopener noreferrer"
              className="howitworks-cta-btn"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a1.5 1.5 0 0 1-.61-1.21V3.024c0-.48.24-.91.609-1.21zM14.5 12.707l2.302 2.302-11.34 6.545 9.038-8.847zM19.79 8.51l-2.83 1.634L14.5 12l2.46 2.46 2.83 1.634a1.5 1.5 0 0 0 0-2.598v.014zM5.462 2.446L16.802 8.99 14.5 11.293 5.462 2.446z"/>
              </svg>
              Google Play
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HowItWorks;